import { __decorate, __metadata } from "tslib";
import { Serialize } from '@ftr/serialization';
import { LocalTime } from '@js-joda/core';
/**
 * The duration of a job's turnaround
 */
export class TurnaroundDuration {
    constructor(length, unit) {
        this.length = length;
        this.unit = unit;
    }
    /**
     * Returns true if the length and unit of the current object equal those of the other object.
     */
    equals(other) {
        return this.length === other.length && this.unit === other.unit;
    }
    /**
     * Returns `true` if the value of this TurnaroundDuration is less than the value of `other`,
     * otherwise returns `false`. Note this function takes the `unit` into account however
     * Business Days and Calendar Days are considered the same.
     *
     * 4 Business Hours < 2  Business Days   // true
     * 4 Business Hours < 10 Business Days   // true
     * 2 Calendar Days  < 2  Business Days   // false
     *
     * @param other The TurnaroundDuration to compare to.
     */
    lessThan(other) {
        const thisLengthInHours = getTurnaroundLengthInHours(this.length, this.unit);
        const otherLengthInHours = getTurnaroundLengthInHours(other.length, other.unit);
        return thisLengthInHours < otherLengthInHours;
    }
}
__decorate([
    Serialize(() => Number),
    __metadata("design:type", Number)
], TurnaroundDuration.prototype, "length", void 0);
/**
 * Specifies the unit of time for a turnaround that's used when calculating a due date
 */
export var TurnaroundUnit;
(function (TurnaroundUnit) {
    /**
     * The turnaround unit refers to business days only, excluding weekends and the court system's holidays
     */
    TurnaroundUnit["BusinessDays"] = "business days";
    /**
     * The turnaround unit refers to calendar days, including weekends and holidays
     */
    TurnaroundUnit["CalendarDays"] = "calendar days";
    /**
     * The turnaround unit refers to business hours, i.e. hours between the court system's start and end of day times
     */
    TurnaroundUnit["BusinessHours"] = "business hours";
})(TurnaroundUnit || (TurnaroundUnit = {}));
/**
 * Returns true if the given offset is less than the given duration.
 * Also returns true for same-day turnarounds (i.e. duration is 0) if the offset is in business hours.
 */
export function offsetIsValid(offset, duration) {
    return duration.length === 0 && offset.unit === TurnaroundUnit.BusinessHours ? true : offset.lessThan(duration);
}
function getTurnaroundLengthInHours(length, unit) {
    return unit === TurnaroundUnit.BusinessHours ? length : length * LocalTime.HOURS_PER_DAY;
}
